import * as angular from "angular";
import {IController} from "angular";
import * as _ from "lodash";
import {ConfirmDialogService} from "../../../commons/services/confirm-dialog.service";
import {SiteContextService} from "../../../commons/services/site-context.service";
import {ServiceAreaDialogService} from "../../services/service-area-dialog-service.service";
import {PolygonService} from "../../services/polygons-service.service";
import {GeofenceLocationType} from "../../models/geofence-zone.model";
import {$accountsFactory} from "../../../account/$accounts.factory";

export class editGeofenceZoneViewCtrl implements IController {

  feature: any;
  isNew: boolean;
  locationType: GeofenceLocationType;
  excludeIds: string[];
  onCancel: Function;
  onChange: Function;
  onSave: Function;

  rangeKm: number;
  locationAddressText: string;

  newGeofenceTitle: string;
  selectorPlaceholder: string;
  isAccount: boolean;
  isDepot: boolean;
  savePermission: string;

  private static readonly KM = 1000;
  private static readonly DEFAULT_RADIUS_METERS = 200;

  private accountsRef: any;
  private loadedAddressLocationId: string;

  /* @ngInject */
  constructor(private confirmDialogService: ConfirmDialogService,
              private $log,
              private serviceAreaDialogService: ServiceAreaDialogService,
              private polygonService: PolygonService,
              private siteContextService: SiteContextService,
              private $accounts: $accountsFactory,
              private $filter) {
  }

  $onInit() {
    this.accountsRef = this.$accounts();
    this._syncRangeKmFromFeature();
    this._refreshLocationLabels();
    this._refreshSavePermission();
    this._loadSelectedLocationAddress();
  }

  $onChanges(changes) {
    if (changes.feature) {
      this._syncRangeKmFromFeature();
      if (!changes.feature.isFirstChange()) {
        this._loadSelectedLocationAddress();
      }
    }
    if (changes.locationType) {
      this._refreshLocationLabels();
      if (!changes.locationType.isFirstChange()) {
        this._loadSelectedLocationAddress();
      }
    }
    if (changes.isNew) {
      this._refreshSavePermission();
    }
  }

  onLocationChange(location, id) {
    this._setLocationAddress(location);
    this.loadedAddressLocationId = id;

    const featureCopy = angular.copy(this.feature);
    featureCopy.properties.locationId = id;
    featureCopy.properties.areaName = _.get(location, 'name');

    if (!id) {
      featureCopy.properties.center = null;
      featureCopy.geometry.coordinates = [];
      this.onChange({feature: featureCopy});
      return;
    }

    const addressLocation = _.get(location, 'address.location', _.get(location, 'location'));
    const lat = _.get(addressLocation, 'latitude', _.get(addressLocation, 'lat'));
    const lng = _.get(addressLocation, 'longitude', _.get(addressLocation, 'lng'));
    if (_.isFinite(lat) && _.isFinite(lng)) {
      featureCopy.properties.center = {latitude: lat, longitude: lng};
      if (this.isNew && !_.isFinite(featureCopy.properties.range)) {
        const defaultRadiusMeters = this._getDefaultRadiusMeters();
        if (_.isFinite(defaultRadiusMeters) && defaultRadiusMeters > 0) {
          featureCopy.properties.range = defaultRadiusMeters;
          this.rangeKm = defaultRadiusMeters / editGeofenceZoneViewCtrl.KM;
        }
      }
      if (featureCopy.properties.range) {
        featureCopy.geometry.coordinates = this.polygonService.getCircleGeoJsonCoordinates(
          featureCopy.properties.center, featureCopy.properties.range);
      }
    }
    this.onChange({feature: featureCopy});
  }

  onRangeKmChange() {
    const radiusMeters = this.rangeKm ? this.rangeKm * editGeofenceZoneViewCtrl.KM : null;
    const featureCopy = angular.copy(this.feature);
    featureCopy.properties.range = radiusMeters;
    if (featureCopy.properties.center && radiusMeters) {
      featureCopy.geometry.coordinates = this.polygonService.getCircleGeoJsonCoordinates(
        featureCopy.properties.center, radiusMeters);
    }
    this.onChange({feature: featureCopy});
  }

  onUploadJSON(event) {
    this.serviceAreaDialogService.uploadGeoJSON(event, this.feature)
      .then((geoJson) => {
        if (geoJson && geoJson.geometry) {
          this._onSetGeoJSON(geoJson.geometry);
        }
      });
  }

  onSaveClicked(form, event) {
    form.$setSubmitted(true);
    if (!form.$valid) {
      return;
    }
    this.confirmDialogService.confirm('Save Geofence', 'Are you sure you want to save the changes made to this geofence?', event)
      .then(() => {
        this.onSave({feature: this.feature});
      })
      .catch(() => {
        this.$log.info('Dialog Closed');
      });
  }

  // ******************************
  //        Private methods
  // ******************************

  private _syncRangeKmFromFeature() {
    const rangeMeters = _.get(this.feature, 'properties.range');
    this.rangeKm = _.isFinite(rangeMeters) ? rangeMeters / editGeofenceZoneViewCtrl.KM : null;
  }

  private _refreshLocationLabels() {
    this.isAccount = this.locationType === GeofenceLocationType.ACCOUNT;
    this.isDepot = this.locationType === GeofenceLocationType.DEPOT;
    this.newGeofenceTitle = this.isAccount
      ? 'New Account Geofence'
      : this.isDepot ? 'New Depot Geofence' : 'New Geofence';
    this.selectorPlaceholder = this.isAccount
      ? 'Account'
      : this.isDepot ? 'Depot' : 'Location';
  }

  private _refreshSavePermission() {
    this.savePermission = this.isNew ? 'geofence.zones.create' : 'geofence.zones.update';
  }

  private _getDefaultRadiusMeters() {
    const radius = _.get(this.siteContextService.getSiteInfo(), 'geofencingArrivalTaskRadius');
    if (_.isFinite(radius) && radius > 0) {
      return radius;
    }
    return editGeofenceZoneViewCtrl.DEFAULT_RADIUS_METERS;
  }

  private _setLocationAddress(location) {
    const address = _.get(location, 'address');
    this.locationAddressText = address ? this.$filter('bringozAddress')(address) : '';
  }

  private _loadSelectedLocationAddress() {
    if (!this.isAccount || !this.accountsRef) {
      this.locationAddressText = '';
      return;
    }

    const locationId = _.get(this.feature, 'properties.locationId');
    if (!locationId) {
      this.locationAddressText = '';
      return;
    }
    if (locationId === this.loadedAddressLocationId) {
      return;
    }
    this.loadedAddressLocationId = locationId;

    this.accountsRef.$asyncValues([locationId])
      .then((accounts) => {
        if (_.get(this.feature, 'properties.locationId') !== locationId) {
          return;
        }
        this._setLocationAddress(accounts && accounts[0]);
      })
      .catch((error) => {
        this.$log.error('Failed to get account for geofence zone', error);
        this.loadedAddressLocationId = undefined;
        this.locationAddressText = '';
      });
  }

  private _onSetGeoJSON(geometry) {
    if (geometry.type === 'MultiPolygon') {
      let coordsIndex = 0;
      let length = 0;

      _.forEach(geometry.coordinates, (c, index) => {
        if (length < c[0].length) {
          coordsIndex = Number(index);
          length = c[0].length;
        }
      });
      geometry.coordinates = geometry.coordinates[coordsIndex];
    }

    const featureCopy = angular.copy(this.feature);
    featureCopy.geometry.coordinates = _.get(geometry, 'coordinates');
    featureCopy.geometry.type = 'Polygon';

    this.onChange({feature: featureCopy});
  }

}

angular
  .module('bringoz.admin.service-areas')
  .component('editGeofenceZoneView', {
    bindings: {
      feature: '<',
      isNew: '<',
      locationType: '<',
      excludeIds: '<',
      onCancel: '&',
      onChange: '&',
      onSave: '&'
    },
    template: require('./edit-geofence-zone-view.html').default,
    controller: editGeofenceZoneViewCtrl
  });
